'use client';

import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { LuSend, LuLoader } from 'react-icons/lu';
import { useUser } from '@clerk/nextjs';

const services = [
  'Land Survey',
  'GIS Services',
  'Property Management',
  'Cyber Services',
  'Qwetu Gas Supplier',
];

export default function ContactForm() {
  const { user, isSignedIn } = useUser();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });

  useEffect(() => {
    if (isSignedIn && user) {
      setFormData((prev) => ({
        ...prev,
        name: user.fullName || '',
        email: user.primaryEmailAddress?.emailAddress || '',
      }));
    }
  }, [isSignedIn, user]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(
    null
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Submit to our integrated contact API that handles Clerk user creation and booking
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      // Check if we got a proper JSON response
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Response is not JSON:', text.substring(0, 200));
        throw new Error('Unexpected response from server');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit contact form');
      }

      // Success! Clear the form and show success message
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });

      console.log('Contact form submitted successfully', {
        isNewUser: data.isNewUser,
        bookingId: data.booking?.id,
      });
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <Card className='overflow-hidden rounded-xl shadow-lg bg-gray-800/50 backdrop-blur-xs border border-gray-700'>
      <form onSubmit={handleSubmit} className='p-6 space-y-6'>
        <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
          <div className='space-y-2'>
            <label htmlFor='name' className='text-sm font-medium text-gray-300'>
              Your Name
            </label>
            <Input
              id='name'
              name='name'
              value={formData.name}
              onChange={handleChange}
              placeholder='John Doe'
              readOnly={isSignedIn}
              className={`bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 ${
                errors.name
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:border-blue-400 focus:ring-blue-400'
              }`}
            />
            {errors.name && (
              <p className='text-sm text-red-400'>{errors.name}</p>
            )}
          </div>

          <div className='space-y-2'>
            <label
              htmlFor='email'
              className='text-sm font-medium text-gray-300'
            >
              Your Email
            </label>
            <Input
              id='email'
              name='email'
              type='email'
              value={formData.email}
              onChange={handleChange}
              placeholder='john@example.com'
              readOnly={isSignedIn}
              className={`bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 ${
                errors.email
                  ? 'border-red-500 focus:ring-red-500'
                  : 'focus:border-blue-400 focus:ring-blue-400'
              }`}
            />
            {errors.email && (
              <p className='text-sm text-red-400'>{errors.email}</p>
            )}
          </div>
        </div>

        <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
          <div className='space-y-2'>
            <label
              htmlFor='phone'
              className='text-sm font-medium text-gray-300'
            >
              Phone Number (Optional)
            </label>
            <Input
              id='phone'
              name='phone'
              type='tel'
              value={formData.phone}
              onChange={handleChange}
              placeholder='+254 XXX XXX XXX'
              className='bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 focus:border-blue-400 focus:ring-blue-400'
            />
          </div>

          <div className='space-y-2'>
            <label
              htmlFor='service'
              className='text-sm font-medium text-gray-300'
            >
              Service Interested In
            </label>
            <select
              id='service'
              name='service'
              value={formData.service}
              onChange={handleChange}
              className='w-full rounded-md border border-gray-600 bg-gray-700/50 py-2 px-3 shadow-xs focus:border-blue-400 focus:outline-hidden focus:ring-blue-400 text-sm text-white'
            >
              <option value=''>Select a service</option>
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='space-y-2'>
          <label
            htmlFor='message'
            className='text-sm font-medium text-gray-300'
          >
            Your Message
          </label>
          <Textarea
            id='message'
            name='message'
            value={formData.message}
            onChange={handleChange}
            placeholder='How can we help you?'
            rows={5}
            className={`bg-gray-700/50 border-gray-600 text-white placeholder:text-gray-400 ${
              errors.message
                ? 'border-red-500 focus:ring-red-500'
                : 'focus:border-blue-400 focus:ring-blue-400'
            }`}
          />
          {errors.message && (
            <p className='text-sm text-red-400'>{errors.message}</p>
          )}
        </div>

        {submitStatus === 'success' && (
          <div className='rounded-md bg-emerald-900/50 border border-emerald-700 p-4'>
            <div className='flex'>
              <div className='shrink-0'>
                <svg
                  className='h-5 w-5 text-emerald-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <p className='text-sm font-medium text-emerald-300'>
                  Thank you! Your message has been sent successfully.
                </p>
              </div>
            </div>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className='rounded-md bg-red-900/50 border border-red-700 p-4'>
            <div className='flex'>
              <div className='shrink-0'>
                <svg
                  className='h-5 w-5 text-red-400'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path
                    fillRule='evenodd'
                    d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <div className='ml-3'>
                <p className='text-sm font-medium text-red-300'>
                  Sorry, there was an error sending your message. Please try
                  again.
                </p>
              </div>
            </div>
          </div>
        )}

        <button
          type='submit'
          disabled={isSubmitting}
          className='w-full rounded-full bg-linear-to-r from-blue-500 to-purple-500 px-4 py-3 text-sm font-medium text-white shadow-xs hover:from-blue-600 hover:to-purple-600 focus:outline-hidden focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center'
        >
          {isSubmitting ? (
            <>
              <LuLoader className='mr-2 h-4 w-4 animate-spin' />
              Sending...
            </>
          ) : (
            <>
              <LuSend className='mr-2 h-4 w-4' />
              Send Message
            </>
          )}
        </button>
      </form>
    </Card>
  );
}
